'use client'

import { useExploreFilters } from '@/hooks/use-explore-filters'
import type { FilterConfig, SortConfig } from '@/lib/merge-stream/types'
import { useMarketsStore } from '@/lib/store/markets-store'
import type { Platform } from '@/types/market'
import { useEffect, useMemo, useRef } from 'react'

function syncAndPrefetch(
	sort: SortConfig,
	filters: FilterConfig,
	store: {
		eventsSort: SortConfig
		marketsSort: SortConfig
		eventsFilters: FilterConfig
		marketsFilters: FilterConfig
		events: unknown[]
		markets: unknown[]
		eventsLoading: boolean
		marketsLoading: boolean
		setEventsSort: (s: SortConfig) => void
		setMarketsSort: (s: SortConfig) => void
		setEventsFilters: (f: FilterConfig) => void
		setMarketsFilters: (f: FilterConfig) => void
		loadMoreEvents: () => void
		loadMoreMarkets: () => void
	}
) {
	// Sync sort
	if (sort.key !== store.eventsSort.key || sort.direction !== store.eventsSort.direction) {
		store.setEventsSort(sort)
	}
	if (sort.key !== store.marketsSort.key || sort.direction !== store.marketsSort.direction) {
		store.setMarketsSort(sort)
	}

	// Sync filters — setEventsFilters/setMarketsFilters reset data and call loadMore internally
	const filtersKey = JSON.stringify(filters)

	if (filtersKey !== JSON.stringify(store.eventsFilters)) {
		store.setEventsFilters(filters)
	} else if (store.events.length === 0 && !store.eventsLoading) {
		store.loadMoreEvents()
	}

	if (filtersKey !== JSON.stringify(store.marketsFilters)) {
		store.setMarketsFilters(filters)
	} else if (store.markets.length === 0 && !store.marketsLoading) {
		store.loadMoreMarkets()
	}
}

export function MarketsPrefetch() {
	const { sort, effectiveFilters, enabledPlatforms } = useExploreFilters()

	const filtersWithPlatforms = useMemo(
		() => ({ ...effectiveFilters, platforms: enabledPlatforms as Platform[] }),
		[effectiveFilters, enabledPlatforms]
	)

	const initialSyncDone = useRef(false)

	useEffect(() => {
		if (initialSyncDone.current) return
		initialSyncDone.current = true

		const store = useMarketsStore.getState()
		syncAndPrefetch(sort, filtersWithPlatforms, store)
	}, [sort, filtersWithPlatforms])

	return null
}
